import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../contexts/AuthContext';
import { useSellerStatus, useHasActiveRaffle } from '../../hooks/useSellerStatus';
import { Button, Spinner } from '../../components/ui';
import { createRaffle } from '../../lib/api';

// Wizard Steps
import StepPhotos from './wizard/StepPhotos';
import StepDetails from './wizard/StepDetails';
import StepPricing from './wizard/StepPricing';
import StepSchedule from './wizard/StepSchedule';
import StepLocation from './wizard/StepLocation';
import StepReview from './wizard/StepReview';
import Logo from '../../components/shared/Logo';

/**
 * Raffle creation validation schema
 */
const raffleSchema = z.object({
  // Step 1: Photos
  photos: z.array(z.string().url()).min(1, 'At least 1 photo required').max(8, 'Maximum 8 photos'),

  // Step 2: Details
  title: z.string().min(5, 'Title must be at least 5 characters').max(80, 'Title too long'),
  description: z.string().min(20, 'Description must be at least 20 characters').max(2000, 'Description too long'),
  category: z.enum(['electronics', 'furniture', 'gaming', 'fashion', 'sports', 'collectibles', 'home', 'toys', 'music', 'books', 'vehicles', 'other'], {
    required_error: 'Please select a category',
  }),
  condition: z.enum(['new', 'likeNew', 'good', 'fair'], {
    required_error: 'Please select condition',
  }),

  // Step 3: Pricing
  ticketPrice: z.number().min(100, 'Minimum €1').max(50000, 'Maximum €500'),
  ticketQuantity: z.number().min(10, 'Minimum 10 entries').max(1000, 'Maximum 1000 entries'),

  // Step 4: Schedule
  endsAt: z.date().refine(d => d > new Date(), 'End date must be in the future'),
  minimumThreshold: z.number().min(50).max(100),

  // Step 5: Location
  pickupArea: z.string().min(2, 'Pickup area required'),
  pickupAddress: z.string().min(10, 'Full address required'),
});

const STEPS = [
  { id: 'photos', label: 'Photos', component: StepPhotos },
  { id: 'details', label: 'Details', component: StepDetails },
  { id: 'pricing', label: 'Pricing', component: StepPricing },
  { id: 'schedule', label: 'Schedule', component: StepSchedule },
  { id: 'location', label: 'Location', component: StepLocation },
  { id: 'review', label: 'Review', component: StepReview },
];

/**
 * Create Raffle Page - Multi-Step Wizard
 */
export default function CreateRaffle() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const {
    status,
    isLoading,
    canCreateRaffle,
    needsAuth,
    needsPhoneVerification,
    needsOnboarding,
  } = useSellerStatus();
  const { hasActive, activeRaffleId, loading: activeLoading } = useHasActiveRaffle();

  const [currentStep, setCurrentStep] = useState(0);
  const wasCancelled = searchParams.get('cancelled') === 'true';

  // Form with all fields
  const methods = useForm({
    resolver: zodResolver(raffleSchema),
    defaultValues: {
      photos: [],
      title: '',
      description: '',
      category: undefined,
      condition: undefined,
      ticketPrice: 500, // €5 default
      ticketQuantity: 50,
      endsAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
      minimumThreshold: 80,
      pickupArea: '',
      pickupAddress: '',
    },
    mode: 'onChange',
  });

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!isLoading && needsAuth) {
      navigate('/auth/login?returnUrl=/create');
    }
  }, [isLoading, needsAuth, navigate]);

  // Redirect to phone verification if needed
  useEffect(() => {
    if (!isLoading && needsPhoneVerification) {
      navigate('/auth/verify-phone?returnUrl=/create');
    }
  }, [isLoading, needsPhoneVerification, navigate]);

  // Redirect to onboarding if Stripe Connect not set up
  useEffect(() => {
    if (!isLoading && needsOnboarding) {
      navigate('/seller/onboarding');
    }
  }, [isLoading, needsOnboarding, navigate]);

  if (isLoading || activeLoading) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // User already has an active raffle
  if (hasActive) {
    return (
      <div className="min-h-screen bg-midnight">
        <div className="page-container py-12 md:py-20">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-16 h-16 bg-warning/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">📦</span>
            </div>
            <h1 className="text-3xl font-display text-cream mb-4">
              You Have an Active Competition
            </h1>
            <p className="text-cream/60 mb-8">
              You can only have one active competition at a time.
              Wait for your current competition to end before creating a new one.
              <span className="block mt-2 text-primary/80 text-sm">
                As you receive positive reviews, your active competition limit will increase.
              </span>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/competition/${activeRaffleId}`}>
                <Button>View Your Competition</Button>
              </Link>
              <Link to="/">
                <Button variant="outline">Back to Home</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const CurrentStepComponent = STEPS[currentStep].component;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === STEPS.length - 1;

  function handleNext() {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  }

  function handleBack(stepIndex) {
    // If a specific step index is passed, go directly there
    if (typeof stepIndex === 'number' && stepIndex >= 0 && stepIndex < currentStep) {
      setCurrentStep(stepIndex);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }

  function handleStepClick(index) {
    // Only allow going back, not forward
    if (index < currentStep) {
      setCurrentStep(index);
    }
  }

  async function handleSubmit() {
    // Validate all fields
    const isValid = await methods.trigger();
    if (!isValid) {
      console.error('Form validation failed');
      return;
    }

    const formData = methods.getValues();

    // Call createRaffle Cloud Function
    const result = await createRaffle({
      title: formData.title,
      description: formData.description,
      category: formData.category,
      condition: formData.condition,
      photos: formData.photos,
      ticketPrice: formData.ticketPrice,
      ticketQuantity: formData.ticketQuantity,
      minimumThreshold: formData.minimumThreshold,
      endsAt: formData.endsAt.toISOString(),
      pickupArea: formData.pickupArea,
      pickupAddress: formData.pickupAddress,
    });

    if (result.success && result.raffleId) {
      // Navigate to success page - no listing fee, competition is live immediately
      navigate(`/create/success?raffle_id=${result.raffleId}`);
    } else {
      throw new Error(result.error || 'Failed to create competition');
    }
  }

  return (
    <div className="min-h-screen bg-midnight">
      {/* Header */}
      <header className="border-b border-midnight-lighter bg-midnight/95 backdrop-blur-sm sticky top-0 z-40">
        <div className="page-container">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <Logo size="md" />
            </Link>
            <Link to="/" className="text-cream/50 text-sm hover:text-cream transition-colors">
              Cancel
            </Link>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="border-b border-midnight-lighter bg-midnight-light">
        <div className="page-container py-4">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {STEPS.map((step, index) => (
              <button
                key={step.id}
                onClick={() => handleStepClick(index)}
                disabled={index > currentStep}
                className={`flex flex-col items-center gap-1.5 ${
                  index <= currentStep ? 'cursor-pointer' : 'cursor-not-allowed'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                    index < currentStep
                      ? 'bg-primary text-white'
                      : index === currentStep
                      ? 'bg-primary/20 text-primary border-2 border-primary'
                      : 'bg-midnight-lighter text-cream/40'
                  }`}
                >
                  {index < currentStep ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
                <span
                  className={`text-xs font-medium hidden sm:block ${
                    index <= currentStep ? 'text-cream' : 'text-cream/40'
                  }`}
                >
                  {step.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Cancelled Banner */}
      {wasCancelled && (
        <div className="bg-warning/10 border-b border-warning/30">
          <div className="page-container py-3">
            <p className="text-warning text-sm text-center">
              Payment was cancelled. You can try again when you're ready.
            </p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <FormProvider {...methods}>
        <main className="page-container py-8 md:py-12">
          <div className="max-w-2xl mx-auto">
            <CurrentStepComponent
              onNext={handleNext}
              onBack={handleBack}
              onSubmit={isLastStep ? handleSubmit : undefined}
              isFirstStep={isFirstStep}
              isLastStep={isLastStep}
            />
          </div>
        </main>
      </FormProvider>
    </div>
  );
}
